/**
 * <EconomicCalendar> — 이번 주 미국 경제 지표 발표 일정
 *
 * 데이터: GET /api/ticker/calendar/economic (Perplexity sonar-pro 웹 검색)
 * 필드: datetime (YYYY-MM-DD HH:MM ET), event, importance (높음/중간/낮음)
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Star } from "lucide-react";
import type { WidgetMeta, WidgetProps } from "../types";

interface EconItem {
  datetime: string;
  event: string;
  importance: string;
}

interface EconData {
  updatedAt: string;
  items: EconItem[];
}

const REFRESH_MS = 60 * 60 * 1000;

// 사용자 결정: backend (FRED) 가 이미 KST 시간으로 변환된 "YYYY-MM-DD HH:MM" 형식 반환.
// 별도 ET→KST 변환 불필요 — 그대로 split.
function parseDateTime(datetime: string): { date: string; time: string | null } | null {
  const m = datetime.match(/^(\d{4}-\d{2}-\d{2})(?:\s+(\d{2}:\d{2}))?/);
  if (!m) return null;
  return { date: m[1], time: m[2] || null };
}

function formatDateLabel(dateStr: string): string {
  const [yy, mm, dd] = dateStr.split("-").map(Number);
  const d = new Date(yy, mm - 1, dd);
  const dow = ["일", "월", "화", "수", "목", "금", "토"][d.getDay()];
  return `${mm}/${dd} (${dow})`;
}

function importanceClass(imp: string): "high" | "mid" | "low" {
  if (!imp) return "low";
  if (imp.includes("높") || imp.toLowerCase().includes("high")) return "high";
  if (imp.includes("중") || imp.toLowerCase().includes("mid")) return "mid";
  return "low";
}

// 별 표시 = importance === "높음" (backend FRED release_id 기반 정확한 분류).

export function EconomicCalendar(_props: WidgetProps) {
  const [data, setData] = useState<EconData | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        // 사용자 결정 #7: 캘린더 기간 = 향후 14일 (2주).
        // 이전 7일은 너무 짧아 다음 주 FOMC/CPI 같은 중요 이벤트가 안 보임.
        // 백엔드는 최대 30일까지 허용 — frontend 가 14일 요청.
        const res = await fetch("/api/ticker/calendar/economic?days=14", {
          credentials: "include",
        });
        if (!res.ok) throw new Error();
        const d = (await res.json()) as EconData;
        if (mountedRef.current) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  const items = data?.items || [];

  // 사용자 결정: 실적 캘린더처럼 날짜별 그룹 — KST 변환 후 그룹.
  const groups = useMemo(() => {
    const map: Record<string, { date: string; items: { time: string | null; event: string; importance: string }[] }> = {};
    for (const it of items) {
      // 사용자 결정: 1-star (낮음) 은 노출하지 않음
      const cls = importanceClass(it.importance);
      if (cls === "low") continue;
      const parsed = parseDateTime(it.datetime);
      if (!parsed) continue;
      if (!map[parsed.date]) map[parsed.date] = { date: parsed.date, items: [] };
      map[parsed.date].items.push({ time: parsed.time, event: it.event, importance: it.importance });
    }
    return Object.values(map)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 14); // 사용자 결정 #7: 14일치 노출
  }, [items]);

  return (
    <div className="dw-body dw-econ">
      <div className="dw-head">
        <span className="dw-title">경제 일정</span>
        <span className="dw-cal-range">향후 2주</span>
      </div>
      <div className="dw-econ-body">
        {loading && items.length === 0 ? (
          <>
            {[0, 1, 2].map((i) => (
              <div key={i} className="dw-econ-group is-loading">
                <span className="bs-skel" style={{ width: 60, height: 10, display: "block", marginBottom: 6 }} />
                <span className="bs-skel" style={{ width: "70%", height: 11, display: "block" }} />
              </div>
            ))}
          </>
        ) : groups.length === 0 ? (
          <div className="dw-cal-empty">예정된 발표 없음</div>
        ) : (
          groups.map((g) => (
            <div key={g.date} className="dw-econ-group">
              <div className="dw-econ-date">{formatDateLabel(g.date)}</div>
              <div className="dw-econ-events">
                {g.items.map((it, i) => {
                  const cls = importanceClass(it.importance);
                  // 사용자 결정 #13: 별 3 = 별 1개 강조 / 별 2 = 그냥 표시 (spacer) / 별 1 = 위에서 filter 됨
                  const isHigh = cls === "high";
                  return (
                    <div key={i} className={`dw-econ-item imp-${cls}`}>
                      <span className="dw-econ-time">{it.time || "—"}</span>
                      {isHigh ? (
                        <Star size={10} className="dw-econ-star" fill="currentColor" strokeWidth={0} />
                      ) : (
                        <span className="dw-econ-spacer" />
                      )}
                      <span className="dw-econ-event" title={it.event}>
                        {it.event}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export const economicCalendarMeta: WidgetMeta = {
  type: "economic-calendar",
  name: "경제 일정",
  description: "이번 주 FOMC · CPI · 고용지표 · GDP",
  icon: "📆",
  category: "info",
  defaultSize: { w: 6, h: 4 },
  availableSizes: [
    { w: 4, h: 4 },
    { w: 6, h: 4 },
    { w: 6, h: 6 },
  ],
  minW: 4,
  maxW: 12,
  minH: 3,
  maxH: 10,
  component: EconomicCalendar,
};
